import React, { useState, useCallback, useMemo } from 'react';
import { ChatBulkAuditBatch, AttemptAuditResults } from '../SpeedAudit/types';
import { Typography, Divider, Button, Tag, TextArea, Banner, Spinner } from '@scale/scaleui';
import {
  Badge,
  Link,
  DataList,
  Flex,
  Card,
  Text,
  Heading,
  Icon,
  Tooltip,
} from '@scale/scaleui-radix';
// import Link from '@scale/frontend-lib/link';
import { AuditResultV2 } from '@scale/shared/defaults';
import { submitBulkChatAudit } from './ChatBulkAuditApi';
import BulkAssignTaskActionsModal from './BulkAssignTaskActionsModal';
import { ChatBulkAuditTaskAction } from '@scale/shared/types/corp/attemptAudit';
import { makeStyles } from 'react-scale/components/design-system';
import { IUserJson } from '@scale/shared/types/user';
import { useAuditActions } from 'react-scale/components/ExpertManagement/stores/audit.store';
import {
  useFetchUserInfo,
  useAuditorPermissions,
} from 'react-scale/components/audits/ChatBulkAudit/apiClient';
import {
  getAuditPageReturnUrl,
  isRemoOrOutlierAdmin,
} from 'react-scale/components/audits/ChatBulkAudit/utils';

import { QAOperationHeader } from 'react-scale/components/remoAdmin/qaOperation/QAOperationSubmitModal';
import { useQAOperationContext } from 'react-scale/components/remoAdmin/qaOperation/qaOperationContext';
import DataCompassWithInitialCards from 'react-scale/corp/views/DataCompassV2/DataCompassWithInitialCards';
import { CardType } from '@scale/genai-compass-shared/cardTypes';
import { CompassVariant } from '@scale/genai-compass-shared/compassVariants';
import type { ChatBulkAuditResultsAdapterContext } from '@scale/genai-compass-shared/cards/chat_bulk_audit_results_adapter';
import { faArrowLeft, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { GenAIOpsHubPaths } from 'react-scale/corp/views/CorpProjectGroupManager/constants';
import moment from 'moment';
import _ from 'lodash';

const getDateWithFormat = (date?: string | Date) => {
  return date ? new Date(date).toLocaleString() + ' (' + moment(date).fromNow() + ')' : undefined;
};

const MAX_ERROR_CATEGORIES = 3;
const MAX_ERROR_CATEGORY_STRING_LENGTH = 11;
const MAX_TASK_ATTEMPT_ID_STRING_LENGTH = 6;
const MAX_RATING_STRING_LENGTH = 17;
const PDR_THRESHOLD = 2;

type AuditViewType = 'chat_bulk_audit' | 'standard' | 'SME' | 'text_based';

export const hrefMapping: Record<AuditViewType, string> = {
  chat_bulk_audit: '/corp/chat_bulk_audit/',
  standard: '/corp/v2/speed_audit/',
  SME: '/corp/sme_audit/',
  text_based: '/corp/v2/speed_audit/',
};

const ratingMap: Record<AuditResultV2, string> = {
  perfect: 'Perfect (5-star)',
  good: 'Good (4-star)',
  ok: 'Ok (3-star)',
  bad: 'Bad (2-star)',
  terrible: 'Terrible (1-star)',
  undecided: 'Undecided',
};
type PartialAuditResultV2 = Exclude<AuditResultV2, AuditResultV2.Undecided>;

const ratingToNumber: Record<AuditResultV2, number> = {
  [AuditResultV2.Perfect]: 5,
  [AuditResultV2.Good]: 4,
  [AuditResultV2.Ok]: 3,
  [AuditResultV2.Bad]: 2,
  [AuditResultV2.Terrible]: 1,
  [AuditResultV2.Undecided]: 0,
};

const getRatings = (audits: AttemptAuditResults[]): number[] => {
  return audits
    .map(audit => audit.resultV2?.[0]?.result)
    .filter(
      (rating): rating is PartialAuditResultV2 =>
        rating !== undefined && rating !== AuditResultV2.Undecided,
    )
    .map(rating => ratingToNumber[rating]);
};

const useStyles = makeStyles(() => ({
  bar: {
    height: '40px',
    animation: '$grow 0.8s ease-out',
  },
  '@keyframes grow': {
    from: {
      width: 0,
    },
  },
  banner: {
    width: '1538px',
    marginLeft: '94px',
  },
}));

const FrequencySection = ({
  title,
  data,
  colorClass,
  ratingMap,
}: {
  title: string;
  data: any;
  colorClass: string;
  ratingMap?: any;
}) => {
  const classes = useStyles();

  return (
    <div className="flex flex-col mt-8">
      <Typography.H4>{title}</Typography.H4>
      <div
        className="flex flex-col overflow-auto mt-6 gap-y-4 max-h-[300px]"
        style={{ maxHeight: '350px' }}
      >
        {Object.entries(data).map(([key, frequency], index) => {
          const displayKey = ratingMap ? ratingMap[key] || key : key;
          const frequencyNumber: number = Number(frequency);
          return (
            <div className="flex gap-x-2 items-center" key={index}>
              <Typography.Caption className="text-sm flex gap-x-1 justify-end w-[120px]">
                {displayKey.length > MAX_RATING_STRING_LENGTH
                  ? `${displayKey.substring(0, MAX_RATING_STRING_LENGTH)}...`
                  : displayKey}
              </Typography.Caption>
              <div
                className={`${classes.bar} flex flex-col gap-y-2`}
                style={{
                  width: `${frequencyNumber * 3}px`,
                  backgroundColor: colorClass,
                }}
              ></div>
              <Typography.Caption className="text-sm ml-2">
                {frequencyNumber.toFixed(2)}%
              </Typography.Caption>
            </div>
          );
        })}
      </div>
    </div>
  );
};

function AuditSection({
  title,
  audits,
  subtitle,
}: {
  title: string;
  audits: any[];
  subtitle?: string;
}) {
  return (
    <Card className="flex flex-row items-start">
      <Flex
        align="center"
        justify="between"
        className="flex-shrink-0 w-[300px] flex-col items-start"
      >
        <Text weight="medium">
          {title} ({audits.length} tasks)
        </Text>
        {subtitle && (
          <Text weight="light" as="p" size="2" color="neutral">
            {subtitle}
          </Text>
        )}
      </Flex>
      <div className="flex overflow-auto mt-2 pl-4 w-full" style={{ maxHeight: '200px' }}>
        <div className="flex flex-col w-[140px]">
          <div className="flex flex-col gap-y-2">
            {audits.map(({ taskAttemptId }, index) => (
              <Link key={index} href={`/corp/lookup/${taskAttemptId}`}>
                <a
                  className="inline-flex items-center"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ maxWidth: '60px' }}
                >
                  <Typography.Caption className="text-primary-600 text-base underline">
                    {taskAttemptId.slice(-MAX_TASK_ATTEMPT_ID_STRING_LENGTH)}
                  </Typography.Caption>
                </a>
              </Link>
            ))}
          </div>
        </div>

        <div className="flex flex-col w-[140px]">
          <div className="flex flex-col gap-y-2">
            {audits.map(({ rating }, index) => {
              const displayRating = ratingMap[rating as AuditResultV2] || rating;
              return (
                <Typography.Caption key={index} className="text-neutral-600 text-base">
                  {displayRating === undefined ? '—' : displayRating}
                </Typography.Caption>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex flex-col gap-y-2">
            {audits.map(({ errorCategories }, index) => (
              <div className="flex gap-x-1" key={index}>
                {(errorCategories ?? [])
                  .slice(0, MAX_ERROR_CATEGORIES)
                  .map((errorCategory: any, errorIndex: any) => {
                    const displayText =
                      errorCategory.length > MAX_ERROR_CATEGORY_STRING_LENGTH
                        ? `${errorCategory.substring(0, MAX_ERROR_CATEGORY_STRING_LENGTH)}...`
                        : errorCategory;
                    return (
                      <Tag key={errorIndex} className="" size="sm" variant="neutral">
                        {displayText}
                      </Tag>
                    );
                  })}
                {(errorCategories?.length ?? 0) > MAX_ERROR_CATEGORIES && (
                  <Tag className="" size="sm" variant="neutral">
                    +{(errorCategories?.length ?? 0) - MAX_ERROR_CATEGORIES}
                  </Tag>
                )}
                {(errorCategories?.length ?? 0) === 0 && (
                  <Typography.Caption className="text-neutral-600 text-base">—</Typography.Caption>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

function auditToDisplayFormat(audit: AttemptAuditResults) {
  return {
    rating: audit.resultV2?.[0]?.result,
    errorCategories: audit.resultV2?.[0]?.errorCategories,
    taskAttemptId: audit.auditLookup.id,
  };
}
interface ChatBulkAuditResultsPageProps {
  auditBatch: ChatBulkAuditBatch;
  attemptAuditResultsLocalCopy: AttemptAuditResults[];
  user: IUserJson;
  handleComplete?: () => void;
}

// eslint-disable-next-line complexity
function ChatBulkAuditResultsPage(props: ChatBulkAuditResultsPageProps) {
  const classes = useStyles();

  const { auditBatch, attemptAuditResultsLocalCopy, user } = props;
  const [isAuditCompleted, setIsAuditCompleted] = useState(auditBatch.completed);
  const [auditFeedback, setAuditFeedback] = useState(auditBatch.auditFeedback ?? '');
  const [auditCompletionTime, setAuditCompletionTime] = useState(auditBatch.updatedAt);
  const [auditCompletionUser, setAuditCompletionUser] = useState(auditBatch.auditedBy);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBulkActionsModalOpen, setBulkActionsModalOpen] = useState(false);
  const [isOperationModalOpen, setIsOperationModalOpen] = useState(false);

  const auditCompletionTimeString = useMemo(
    () => getDateWithFormat(auditCompletionTime),
    [auditCompletionTime],
  );

  const closeOperationModal = useCallback(() => setIsOperationModalOpen(false), []);

  const { data: userInfo } = useFetchUserInfo(auditCompletionUser ?? '');

  const { data: auditorPermissions } = useAuditorPermissions();

  const { isInReview, qaOperation } = useQAOperationContext();

  const setOpenChatBulkAuditResults = useAuditActions().setOpenChatBulkAuditResults;

  const isRemoOrOutlierAdminUser = useMemo(() => isRemoOrOutlierAdmin(), []);

  const nonSkippedAttemptAuditResultsLocalCopy = attemptAuditResultsLocalCopy.filter(
    ({ ignored, resultV2, edited }) =>
      !ignored &&
      resultV2?.[0]?.result !== undefined &&
      resultV2?.[0]?.result !== AuditResultV2.Undecided &&
      !edited,
  );
  const skippedAttemptAuditResultsLocalCopy = attemptAuditResultsLocalCopy.filter(
    ({ ignored, resultV2 }) =>
      ignored ||
      resultV2?.[0]?.result === AuditResultV2.Undecided ||
      resultV2?.[0]?.result === undefined,
  );
  const editedAttemptAuditResultsLocalCopy = attemptAuditResultsLocalCopy.filter(
    ({ ignored, edited }) => !ignored && edited,
  );

  const getNonSkippedAudits = useCallback(
    (actions: (string | undefined)[]) =>
      nonSkippedAttemptAuditResultsLocalCopy
        .filter(
          ({ auditAction }) =>
            actions.includes(auditAction) ||
            (actions.includes(undefined) && auditAction === undefined),
        )
        .map(({ auditLookup, resultV2 }) => ({
          taskAttemptId: auditLookup.id,
          rating: resultV2?.[0]?.result,
          errorCategories: resultV2?.[0]?.errorCategories,
        })),
    [nonSkippedAttemptAuditResultsLocalCopy],
  );
  const skippedAudits = skippedAttemptAuditResultsLocalCopy.map(({ auditLookup, resultV2 }) => ({
    taskAttemptId: auditLookup.id,
    rating: undefined,
    errorCategories: resultV2?.[0]?.errorCategories,
  }));

  const editedAudits = editedAttemptAuditResultsLocalCopy.map(({ auditLookup, resultV2 }) => ({
    taskAttemptId: auditLookup.id,
    rating: resultV2?.[0]?.result,
    errorCategories: resultV2?.[0]?.errorCategories,
  }));

  const SBQAudits = getNonSkippedAudits([
    ChatBulkAuditTaskAction.SEND_BACK_TO_QUEUE_CLEAR_RESPONSES,
  ]);

  // We need to group the audits by the review level they are being moved to
  const moveToSpecificReviewLevelAudit = useMemo(() => {
    const withKeepResponsesAction = nonSkippedAttemptAuditResultsLocalCopy.filter(
      ({ auditAction, sendToReviewLevel }) =>
        auditAction === ChatBulkAuditTaskAction.SEND_BACK_TO_QUEUE_KEEP_RESPONSES &&
        sendToReviewLevel !== undefined,
    );
    const groupedByReviewLevel = _.groupBy(withKeepResponsesAction, 'sendToReviewLevel');

    return Object.entries(groupedByReviewLevel).map(([reviewLevel, audits]) => {
      return {
        reviewLevel,
        audits: audits.map(auditToDisplayFormat),
      };
    });
  }, [nonSkippedAttemptAuditResultsLocalCopy]);

  const moveForwardAudits = useMemo(() => {
    const explicitMoveForwardAudits = getNonSkippedAudits([
      ChatBulkAuditTaskAction.MOVE_FORWARD_ONE_LAYER,
    ]);
    const allMoveForwardAudits = _.uniqBy(
      [...explicitMoveForwardAudits, ...editedAudits],
      'taskAttemptId',
    );
    return allMoveForwardAudits;
  }, [getNonSkippedAudits, editedAudits]);

  const keepInCurrentLevelAudits = useMemo(
    () => getNonSkippedAudits([ChatBulkAuditTaskAction.KEEP_IN_CURRENT_LEVEL, undefined]),
    [getNonSkippedAudits],
  );

  const keepInReviewLevelOrNotRatedAudits = useMemo(
    () =>
      // Edited audits are moving forward by one review level, so we don't want to include them here
      [...keepInCurrentLevelAudits, ...skippedAudits].filter(
        audit =>
          !editedAudits.some(editedAudit => editedAudit.taskAttemptId === audit.taskAttemptId),
      ),
    [keepInCurrentLevelAudits, skippedAudits, editedAudits],
  );

  const errorCategoriesFrequency: Record<string, number> = {};
  attemptAuditResultsLocalCopy.forEach(({ resultV2 }) => {
    resultV2?.[0]?.errorCategories?.forEach((errorCategory: any) => {
      errorCategoriesFrequency[errorCategory] = (errorCategoriesFrequency[errorCategory] ?? 0) + 1;
    });
  });
  Object.entries(errorCategoriesFrequency).forEach(([errorCategory, count]) => {
    errorCategoriesFrequency[errorCategory] =
      (count / attemptAuditResultsLocalCopy.length || 0) * 100;
  });

  const ratingsFrequency: Record<string, number> = {};
  nonSkippedAttemptAuditResultsLocalCopy.forEach(({ resultV2 }) => {
    const rating = resultV2?.[0]?.result ?? 'N/A';
    ratingsFrequency[rating] = (ratingsFrequency[rating] ?? 0) + 1;
  });
  Object.entries(ratingsFrequency).forEach(([rating, count]) => {
    ratingsFrequency[rating] = (count / nonSkippedAttemptAuditResultsLocalCopy.length || 0) * 100;
  });

  const taskAttemptIdsWithoutRating: string[] = [];
  nonSkippedAttemptAuditResultsLocalCopy.forEach(({ resultV2, auditLookup }) => {
    if (resultV2?.[0]?.result === undefined || resultV2?.[0]?.result === AuditResultV2.Undecided) {
      taskAttemptIdsWithoutRating.push(auditLookup.id);
    }
  });

  const href =
    auditBatch.auditViewType !== undefined && auditBatch.auditViewType in hrefMapping
      ? hrefMapping[auditBatch.auditViewType as AuditViewType]
      : '/corp/chat_bulk_audit/';

  const shouldShowCompass = useMemo(() => {
    const remoAdminUser = isRemoOrOutlierAdmin();
    if (remoAdminUser) {
      return auditorPermissions?.hasTaskActionsPermissions;
    }
    return true;
  }, [auditorPermissions?.hasTaskActionsPermissions]);

  const auditBatchId = auditBatch?._id;

  const cards = useMemo(
    () => [
      {
        cardType: CardType.CHAT_BULK_AUDIT_RESULTS_ADAPTER,
        auditBatchId,
      } satisfies ChatBulkAuditResultsAdapterContext,
    ],
    [auditBatchId],
  );

  const { averageRating, pdrRating } = useMemo(() => {
    const ratings = getRatings(nonSkippedAttemptAuditResultsLocalCopy);
    const pdrRatings = ratings.filter(x => x <= PDR_THRESHOLD);

    return {
      averageRating: ratings.length > 0 ? _.mean(ratings).toFixed(2) : null,
      pdrRating:
        pdrRatings.length > 0
          ? ((pdrRatings.length / ratings.length) * 100).toFixed(2) + '%'
          : null,
    };
  }, [nonSkippedAttemptAuditResultsLocalCopy]);

  return (
    <div className="flex flex-col pb-6">
      {qaOperation && (
        <QAOperationHeader
          isOpen={isOperationModalOpen}
          overrideSubmitButton={!isInReview() && !isAuditCompleted}
          onClose={closeOperationModal}
        />
      )}
      {/* // TODO make this more pretty */}
      {isAuditCompleted ? null : (
        <div
          className="text-left mt-8 pl-8"
          onClick={() => {
            if (!window.location.pathname.includes('corp')) {
              setOpenChatBulkAuditResults(false);
            }
          }}
        >
          <a
            href={getAuditPageReturnUrl(href, auditBatch._id)}
            className="flex items-center w-full hover:underline"
          >
            <Button variant="outline" color="info" className="flex flex-row gap-2">
              <Icon label="Back to audit" icon={faArrowLeft} size="2" />
              <Text size="2">Back to audit</Text>
            </Button>
          </a>
        </div>
      )}
      {taskAttemptIdsWithoutRating.length > 0 && (
        <div className={`p-2 mx-auto ${classes.banner}`}>
          <Banner
            background="white"
            backgroundVariant="white"
            buttonVariant="neutral"
            subtitle={`Ids without rating: ${taskAttemptIdsWithoutRating.join(', ')}`}
            title={`${taskAttemptIdsWithoutRating.length} task have not been audited. Please audit all tasks before submitting.`}
            variant="danger"
          />
        </div>
      )}

      <div className="flex items-start self-center justify-center w-full gap-x-6 py-8">
        {/* Left side component */}
        <Card size="2" className="p-8 flex flex-col gap-4 w-1/3">
          <Heading weight="medium">Audit Summary</Heading>
          <Flex justify="center" align="center" className="w-full">
            <DataList.Root
              className="w-full"
              orientation={{ initial: 'vertical', sm: 'horizontal' }}
            >
              <DataList.Item className="flex flex-row justify-between">
                <DataList.Label minWidth="88px">Audit Status</DataList.Label>
                <DataList.Value>
                  {isAuditCompleted ? (
                    <Badge color="success">
                      <Text weight="medium">Completed</Text>
                    </Badge>
                  ) : (
                    <Badge color="info">
                      <Text weight="medium">Pending Completion</Text>
                    </Badge>
                  )}
                </DataList.Value>
              </DataList.Item>

              {isAuditCompleted ? (
                <DataList.Item className="flex flex-row justify-between">
                  <DataList.Label minWidth="88px">Completed by</DataList.Label>
                  <DataList.Value>
                    {userInfo ? (
                      <div className="flex flex-col">
                        {isRemoOrOutlierAdminUser ? (
                          <Text size="2">{userInfo.email}</Text>
                        ) : (
                          <Link
                            // Chat bulk audit results page is currently hosted on corp so we need to always redirect to the corp version to be safe
                            href={`corp/${GenAIOpsHubPaths.OpsSearch.replace(
                              ':searchId',
                              userInfo.id,
                            )}`}
                            className="text-primary-600 hover:underline"
                          >
                            <Text size="2">{userInfo.email}</Text>
                          </Link>
                        )}
                      </div>
                    ) : (
                      'Unknown'
                    )}
                  </DataList.Value>
                </DataList.Item>
              ) : null}
              {isAuditCompleted ? (
                <DataList.Item className="flex flex-row justify-between">
                  <DataList.Label minWidth="88px">Completed at</DataList.Label>
                  <DataList.Value>{auditCompletionTimeString}</DataList.Value>
                </DataList.Item>
              ) : null}

              {isAuditCompleted ? (
                <DataList.Item className="flex flex-row justify-between">
                  <DataList.Label minWidth="88px">Audit Feedback (Internal)</DataList.Label>
                  <DataList.Value>
                    {auditFeedback === '' ? 'No feedback given' : auditFeedback}
                  </DataList.Value>
                </DataList.Item>
              ) : null}
              <DataList.Item className="flex flex-row justify-between">
                <DataList.Label minWidth="88px">Tasks Audited Without Edits</DataList.Label>
                <DataList.Value>{nonSkippedAttemptAuditResultsLocalCopy.length}</DataList.Value>
              </DataList.Item>
              <DataList.Item className="flex flex-row justify-between">
                <DataList.Label minWidth="88px">Tasks Not Rated</DataList.Label>
                <DataList.Value>{skippedAttemptAuditResultsLocalCopy.length}</DataList.Value>
              </DataList.Item>
              <DataList.Item className="flex flex-row justify-between">
                <DataList.Label minWidth="88px">Tasks Edited</DataList.Label>
                <DataList.Value>{editedAttemptAuditResultsLocalCopy.length}</DataList.Value>
              </DataList.Item>

              {averageRating && (
                <DataList.Item className="flex flex-row justify-between">
                  <DataList.Label
                    minWidth="88px"
                    className="flex flex-row items-center justify-start"
                  >
                    Average Quality
                    <Tooltip content="Average Quality of this CBA based on valid scores">
                      <Icon
                        className="ml-1.5"
                        size="1"
                        label="average quality"
                        icon={faCircleInfo}
                        color="info"
                      />
                    </Tooltip>
                  </DataList.Label>
                  <DataList.Value>{averageRating}</DataList.Value>
                </DataList.Item>
              )}
              {pdrRating && (
                <DataList.Item className="flex flex-row justify-between">
                  <DataList.Label
                    minWidth="88px"
                    className="flex flex-row items-center justify-start"
                  >
                    PDR
                    <Tooltip content="PDR of this CBA based on valid scores">
                      <Icon
                        className="ml-1.5"
                        size="1"
                        label="PDR"
                        icon={faCircleInfo}
                        color="info"
                      />
                    </Tooltip>
                  </DataList.Label>
                  <DataList.Value>{pdrRating}</DataList.Value>
                </DataList.Item>
              )}
            </DataList.Root>
          </Flex>
          {Object.entries(errorCategoriesFrequency).length > 0 && (
            <>
              <Divider className="mt-8" direction="horizontal" />
              <FrequencySection
                title="Error Categories"
                data={errorCategoriesFrequency}
                colorClass="rgba(141, 131, 255, 1)"
              />
            </>
          )}
          {Object.entries(ratingsFrequency).length > 0 && (
            <>
              <Divider className="mt-8" direction="horizontal" />
              <FrequencySection
                title="Ratings"
                data={ratingsFrequency}
                colorClass="rgba(124, 209, 248, 1)"
                ratingMap={ratingMap}
              />
            </>
          )}
        </Card>

        {/* Right side component */}
        <Card size="2" className="p-8 flex flex-col gap-3 w-2/4">
          {/* Todo this could be done in a prettier way */}
          {auditorPermissions?.hasTaskActionsPermissions && (
            <div className="flex items-center justify-between">
              <Flex direction="column" gap="1">
                <Heading weight="medium">
                  {isAuditCompleted ? 'Details and Actions taken' : 'Pending Actions'}
                </Heading>
                {!isAuditCompleted && (
                  <Text size="2">
                    These actions will be taken after you confirm and complete the audit.
                  </Text>
                )}
              </Flex>
              {!isAuditCompleted && (
                <Button onClick={() => setBulkActionsModalOpen(true)} variant="outline">
                  Bulk Assign Task Actions By Ratings
                </Button>
              )}
            </div>
          )}

          <BulkAssignTaskActionsModal
            isOpen={isBulkActionsModalOpen}
            onClose={() => setBulkActionsModalOpen(false)}
            attemptAudits={nonSkippedAttemptAuditResultsLocalCopy}
          />

          <div className="flex flex-col max-h-[50vh] gap-4 overflow-y-auto">
            {/* Doing this so we have more space on each card, as the header is always the same */}
            <div className="flex items-end mt-3 pl-4 flex-shrink-0 ml-[312px]">
              <Text className="w-[140px]" weight="medium">
                Id
              </Text>
              <Text className="w-[140px]" weight="medium">
                Rating
              </Text>
              <Text weight="medium">Error Categories</Text>
            </div>
            {SBQAudits.length > 0 && <AuditSection title="Send back to queue" audits={SBQAudits} />}
            {moveForwardAudits.length > 0 && (
              <AuditSection title="Move forward by one review level" audits={moveForwardAudits} />
            )}
            {keepInReviewLevelOrNotRatedAudits.length > 0 && (
              <AuditSection
                title="Keep in current review level"
                audits={keepInReviewLevelOrNotRatedAudits}
              />
            )}
            {moveToSpecificReviewLevelAudit.length > 0 &&
              moveToSpecificReviewLevelAudit.map(({ reviewLevel, audits }) => (
                <AuditSection key={reviewLevel} title={`Move to L${reviewLevel}`} audits={audits} />
              ))}
            {editedAttemptAuditResultsLocalCopy.length > 0 && (
              <AuditSection
                title="Edited"
                audits={editedAudits}
                subtitle={
                  'Edited tasks are also listed in the "Move forward by one review level" section.'
                }
              />
            )}
          </div>

          {!isAuditCompleted ? (
            <div className="flex gap-x-8 mt-4 w-full">
              <Typography.H5>Audit Feedback (Internal)</Typography.H5>
              <div className="flex-grow">
                <TextArea
                  value={auditFeedback}
                  onChange={e => setAuditFeedback(e.target.value)}
                  className="w-full"
                  id="text-area"
                  initialResize
                  rows={2}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          ) : null}
          {isAuditCompleted ? (
            <Button
              className="mt-8 w-full py-2.5 flex justify-center flex flex-row gap-2"
              onClick={() => {
                setOpenChatBulkAuditResults(false);
              }}
              size="lg"
              variant="white"
            >
              <a href={getAuditPageReturnUrl(href, auditBatch._id)} className="w-full">
                <Typography.H5>View Audited Tasks</Typography.H5>
              </a>
            </Button>
          ) : (
            <div className="mt-8 w-full px-8">
              <Button
                className="w-full py-2.5 flex justify-center flex flex-row gap-2"
                onClick={async () => {
                  setIsSubmitting(true);
                  try {
                    await submitBulkChatAudit({
                      auditBatchId: auditBatch._id,
                      attemptAudits: attemptAuditResultsLocalCopy,
                      user,
                      auditFeedback,
                    });
                    setAuditCompletionTime(new Date().toISOString());
                    setAuditCompletionUser(user._id);
                    setIsAuditCompleted(true);
                    if (props.handleComplete) props.handleComplete();
                  } finally {
                    setIsSubmitting(false);
                    if (qaOperation) {
                      setIsOperationModalOpen(true);
                    }
                  }
                }}
                disabled={
                  isSubmitting || isAuditCompleted || taskAttemptIdsWithoutRating.length > 0
                }
                size="lg"
                variant="primary"
              >
                {isAuditCompleted ? 'Audit already completed' : 'Confirm and complete audit'}
                {isSubmitting && <Spinner size="sm" />}
              </Button>
            </div>
          )}
        </Card>
      </div>
      {shouldShowCompass && auditBatchId && (
        <div className="h-full flex flex-col mt-8">
          <div className="flex-1 min-h-0 overflow-x-auto py-8 px-14 space-y-4">
            <DataCompassWithInitialCards
              compassVariant={
                isAuditCompleted
                  ? CompassVariant.CHAT_BULK_AUDIT
                  : CompassVariant.CHAT_BULK_AUDIT_PRE_COMPLETION
              }
              initialCardContexts={cards}
              autoExecuteInitialContext={true}
              hideWorkflowHeader={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(ChatBulkAuditResultsPage);
